import { useGetUserNameWithFallback } from '@pba/react-utils';
import { type ProgramEventInvitee, isRegisteredProgramInvitee } from 'models';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Modal, message } from 'ui';
import { useUpdateEventInviteeRoleMutation } from '../../../../queries';

export const useDeleteEventInvitee = () => {
  const { t } = useTranslation();
  const [modal, contextHolder] = Modal.useModal();
  const { isPending: isLoadingDeletingRole, mutateAsync: deleteEventInvitee } = useUpdateEventInviteeRoleMutation();
  const getUserNameWithFallback = useGetUserNameWithFallback();

  const confirm = useCallback(
    ({ eventId, invitee }: { eventId: string; invitee: ProgramEventInvitee }) => {
      // TODO: Find place for this
      const userFullNameOrEmail = isRegisteredProgramInvitee(invitee)
        ? getUserNameWithFallback(invitee)
        : invitee.email;

      const handleDeleteEventInvitee = async () =>
        deleteEventInvitee(
          {
            emailInvitees: isRegisteredProgramInvitee(invitee) ? [] : [{ ...invitee, eventRole: null }],
            eventId,
            invitees: isRegisteredProgramInvitee(invitee) ? [{ ...invitee, eventRole: null }] : [],
          },
          {
            onError() {
              message.error(
                t('Something went wrong. {{name}} couldn’t be removed from event', {
                  name: userFullNameOrEmail,
                }),
              );
            },
            onSuccess() {
              message.success(
                t('{{name}} removed from event successfully', {
                  name: userFullNameOrEmail,
                }),
              );
            },
          },
        );

      modal.confirm({
        content: 'You can invite this user again at any time.',
        icon: <Icon color="error" icon="delete-2-line" />,
        okText: 'Remove',
        onOk: handleDeleteEventInvitee,
        title: t('Are you sure you want to remove {{name}} from this event?', {
          name: userFullNameOrEmail,
        }),
        type: 'error',
      });
    },
    [deleteEventInvitee, getUserNameWithFallback, modal, t],
  );

  return useMemo(
    () => ({ confirm, contextHolder, isLoadingDeletingRole }),
    [confirm, contextHolder, isLoadingDeletingRole],
  );
};
